import { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import {
  LineChart,
  ListChecks,
  BookOpen,
  Briefcase,
  Wallet,
  Cog,
  LogOut,
  Target,
  Radar,
  ShieldCheck,
  Menu,
  X,
  Moon,
  Sun,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { cn } from '@/lib/utils';
import { useTheme } from '@/hooks/use-theme';
import { useAuth } from '@/hooks/use-auth';
import { NichtAngemeldetBanner } from '@/components/NichtAngemeldetBanner';
import { signOut } from '@/lib/auth';
import { usePaperEngineAutoRun } from '@/hooks/use-paper-engine';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { KeyboardShortcutsHelp } from '@/components/KeyboardShortcutsHelp';
import { BUILD_TIME, BUILD_VERSION, PRODUKT_VERSION } from '@/build-info';
import { KONSTANTEN_HASH, REGEL_LABEL, REGEL_VERSION } from '@/lib/v6/setup-state';

// Session 2.1 + Welle 14 Polish: Code-Splitting deutlich aggressiver.
// Nur Dashboard + Watchlist bleiben eager (häufigste Entry-Points).
// Alles andere wird lazy geladen → kleineres Main-Bundle, schnellerer
// First-Paint. Suspense-Fallback zeigt sauberes Loading.
import Dashboard from '@/pages/Dashboard';
import Watchlist from '@/pages/Watchlist';

// Lazy — pro Route eigener JS-Chunk
const SymbolDetail = lazy(() => import('@/pages/SymbolDetail'));
const Signale = lazy(() => import('@/pages/Signale'));
const Positions = lazy(() => import('@/pages/Positions'));
const Journal = lazy(() => import('@/pages/Journal'));
const Settings = lazy(() => import('@/pages/Settings'));
const Strategies = lazy(() => import('@/pages/Strategies'));
const SystemPage = lazy(() => import('@/pages/System'));
const Bestand = lazy(() => import('@/pages/Bestand'));
const BestandDetail = lazy(() => import('@/pages/BestandDetail'));
const AdminCronHealth = lazy(() => import('@/pages/AdminCronHealth'));

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  end?: boolean;
  /** W101 — 'haupt' = der tägliche Trade-Pfad (5 Einträge), 'werkzeuge'
   *  = Power-User-Analyse hinter einem Trenner. */
  section: 'haupt' | 'werkzeuge';
}

const NAV_ITEMS: readonly NavItem[] = [
  // P4/Zielbild (03.07.) — 5 Haupteinträge = der Trichter-Alltag:
  // HEUTE → WATCHLIST (Kandidaten) → TRADE-PLÄNE → DEPOT → JOURNAL (Reflexion).
  // System (Vertrauens-/Operator-Sicht) wandert zu den Werkzeugen — man
  // schaut es wöchentlich an, nicht täglich (Konzept-Audit P4).
  { to: '/', label: 'Heute', icon: LineChart, end: true, section: 'haupt' },
  { to: '/watchlist', label: 'Watchlist', icon: ListChecks, section: 'haupt' },
  // Audit 10.08.2026 — die gemessene Spur gehoert nach vorn. Sie lag als
  // sechster Tab unter „Trade-Plaene", also hinter der Spur, die laut
  // Sperrzeitraum nichts liefert.
  { to: '/signale', label: 'Signale', icon: Radar, section: 'haupt' },
  { to: '/positions', label: 'Depot', icon: Briefcase, section: 'haupt' },
  { to: '/journal', label: 'Journal', icon: BookOpen, section: 'haupt' },
  { to: '/system', label: 'System', icon: ShieldCheck, section: 'werkzeuge' },
  { to: '/bestand', label: 'Bestand', icon: Wallet, section: 'werkzeuge' },
  { to: '/strategies', label: 'Wie entschieden wird', icon: Target, section: 'werkzeuge' },
  { to: '/settings', label: 'Einstellungen', icon: Cog, section: 'werkzeuge' },
];

export default function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();
  const { session, status } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // MM2: Papertrading-Engine automatisch beim App-Start + alle 5 Min laufen.
  usePaperEngineAutoRun();

  // Welle 14 Polish — Globale Keyboard-Shortcuts (g d/w/p/o/s/b/l/j/e/a + ?)
  useKeyboardShortcuts(setShortcutsHelpOpen);

  // Schließe Mobile-Drawer beim Größenwechsel auf Desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 900 && mobileOpen) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [mobileOpen]);

  return (
    <div className="flex min-h-screen bg-cockpit-base text-zinc-100">
      {/* Welle 14 Polish — Keyboard-Shortcuts-Help (öffnet via "?" Taste) */}
      <KeyboardShortcutsHelp open={shortcutsHelpOpen} onClose={() => setShortcutsHelpOpen(false)} />

      {/* Mobile-Top-Bar — sichtbar < lg.
          SICHERER BEREICH (12.08.2026): index.html setzt `viewport-fit=cover`
          und `black-translucent`. Die Seite laeuft damit UNTER die
          iOS-Statusleiste. Diese Leiste sass auf top-0 und war 48 px hoch —
          auf einem iPhone mit Dynamic Island (Statusleiste ~59 px) lag der
          Menue-Knopf vollstaendig darunter: unsichtbar und nicht antippbar.
          Ronni kam deshalb in der Homescreen-App an keine Einstellung heran.
          Der Abstand kommt aus env(safe-area-inset-top) und ist auf Geraeten
          ohne Notch schlicht 0. */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center justify-between border-b border-cockpit-border bg-cockpit-base/95 backdrop-blur px-3 h-12 box-content"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="rounded-md p-1.5 text-zinc-300 hover:bg-cockpit-surface2 focus-ring"
          aria-label="Navigation öffnen"
        >
          <Menu className="size-5" />
        </button>
        <div className="flex items-center gap-1.5">
          <div className="size-6 rounded bg-cockpit-accent/15 border border-cockpit-accent/40 flex items-center justify-center text-cockpit-accent text-xs font-bold">
            S
          </div>
          <span className="text-sm font-semibold tracking-tight">SwingTrader</span>
        </div>
        <div className="w-9" aria-hidden="true" />
      </header>

      {/* Sidebar — schmaler, ruhiger, mit Akzentlinie für aktive Navigation */}
      <aside
        className={cn(
          'fixed lg:static z-40 h-screen w-52 shrink-0 border-r border-cockpit-border bg-cockpit-base/98 lg:bg-cockpit-surface/30 backdrop-blur transition-transform',
          'lg:translate-x-0',
          'overflow-y-auto',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        // Dieselbe Wache fuer die Schublade: Ihr Kopf mit dem Schliessen-Kreuz
        // laege sonst ebenfalls unter der Statusleiste. Unten der Platz fuer
        // den Home-Indikator, sonst ist der letzte Menuepunkt — Einstellungen —
        // nicht erreichbar.
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div className="px-4 py-5 border-b border-cockpit-border flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="size-9 rounded-md bg-gradient-to-br from-cockpit-accent/25 to-cockpit-accent/5 border border-cockpit-accent/30 flex items-center justify-center text-cockpit-accent font-bold">
              S
            </div>
            <div>
              <div className="font-semibold leading-none tracking-tight">SwingTrader</div>
              <div className="text-[10px] text-cockpit-accent/80 mt-1 uppercase tracking-wider font-mono">
                Cockpit
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden rounded-md p-1 text-zinc-400 hover:text-zinc-100 hover:bg-cockpit-surface2 focus-ring"
            aria-label="Navigation schließen"
          >
            <X className="size-4" />
          </button>
        </div>

        <nav className="p-2 flex flex-col gap-0.5">
          {NAV_ITEMS.map(({ to, label, icon: Icon, end, section }, i) => (
            <div key={to} className="contents">
              {/* W101 — Trenner vor dem ersten Werkzeug-Eintrag: die 5
                  Haupt-Punkte oben, Analyse-Tools dezent darunter. */}
              {section === 'werkzeuge' && NAV_ITEMS[i - 1]?.section === 'haupt' && (
                <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-zinc-600">
                  Werkzeuge
                </div>
              )}
            <NavLink
              to={to}
              end={end ?? false}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-all focus-ring',
                  isActive
                    ? 'bg-cockpit-accent-soft text-zinc-50 font-medium'
                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-cockpit-surface2/60'
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-cockpit-accent"
                      aria-hidden="true"
                    />
                  )}
                  <Icon
                    className={cn(
                      'size-4 transition-colors',
                      isActive ? 'text-cockpit-accent' : 'text-zinc-500 group-hover:text-zinc-300'
                    )}
                  />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
            </div>
          ))}
        </nav>

        {/* Sidebar-Footer mit Theme-Toggle und Status */}
        <div className="absolute bottom-0 left-0 right-0 px-3 py-3 border-t border-cockpit-border space-y-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="w-full flex items-center justify-between gap-2 rounded-md border border-cockpit-border bg-cockpit-surface2 hover:bg-cockpit-surface3 px-2.5 py-1.5 text-[11px] focus-ring transition-colors"
            title={theme === 'dark' ? 'In Tagmodus wechseln' : 'In Nachtmodus wechseln'}
            aria-label="Theme umschalten"
          >
            <span className="flex items-center gap-1.5">
              {theme === 'dark' ? (
                <Moon className="size-3.5 text-cockpit-accent" />
              ) : (
                <Sun className="size-3.5 text-cockpit-warning" />
              )}
              <span className="capitalize">
                {theme === 'dark' ? 'Nachtmodus' : 'Tagmodus'}
              </span>
            </span>
            <span className="text-[9px] font-mono uppercase tracking-wider opacity-60">
              wechseln
            </span>
          </button>
          {session?.user?.email && (
            <div className="rounded-md border border-cockpit-border bg-cockpit-surface2 px-2.5 py-1.5 text-[10px] flex items-center justify-between gap-2">
              <span className="truncate text-zinc-300" title={session.user.email}>
                {session.user.email}
              </span>
              <button
                type="button"
                onClick={handleSignOut}
                className="shrink-0 rounded p-1 text-zinc-400 hover:text-zinc-100 hover:bg-cockpit-surface3 focus-ring"
                title="Abmelden"
                aria-label="Abmelden"
              >
                <LogOut className="size-3.5" />
              </button>
            </div>
          )}
          <BuildInfoLine />

          <button
            type="button"
            onClick={() => setShortcutsHelpOpen(true)}
            className="w-full text-[10px] text-zinc-500 hover:text-zinc-300 text-left px-1 transition-colors"
            title="Keyboard-Shortcuts anzeigen (? Taste)"
          >
            ⌨ Shortcuts anzeigen (<kbd className="font-mono">?</kbd>)
          </button>

        </div>
      </aside>

      {/* Backdrop für Mobile-Drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/70 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main */}
      {/* Der Inhalt muss um dieselbe Hoehe nach unten, sonst verschwindet
          seine erste Zeile hinter der Leiste. calc statt fester Zahl, damit
          beide Werte nicht auseinanderlaufen koennen. */}
      <main
        className="flex-1 min-w-0 lg:pt-0"
        style={{ paddingTop: 'calc(3rem + env(safe-area-inset-top, 0px))' }}
      >
        {status === 'anonymous' && <NichtAngemeldetBanner />}
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-64 text-zinc-500 text-sm">
              <div className="animate-pulse">Lade…</div>
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/watchlist" element={<Watchlist />} />
            <Route path="/symbol/:layer/:symbol" element={<SymbolDetail />} />
            <Route path="/signale" element={<Signale />} />
            <Route path="/positions" element={<Positions />} />
            <Route path="/strategies" element={<Strategies />} />
            <Route path="/system" element={<SystemPage />} />
            <Route path="/bestand" element={<Bestand />} />
            <Route path="/bestand/:symbol" element={<BestandDetail />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/settings" element={<Settings />} />
            {/* Audit-Welle A: Cron-Health-Dashboard, gezielter Aufruf via URL */}
            <Route path="/admin/cron-health" element={<AdminCronHealth />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

/**
 * V5.1 — Versions-/Build-Info am Sidebar-Footer.
 * Welle 8 / A8: alpha-Suffix entfernt; nur Engine + Build + Live-Status.
 */
function BuildInfoLine() {
  // Versionsentflechtung 11.08.2026 (Ronnis Entwurf): Produktversion,
  // Signalregel-Version und Build sind DREI verschiedene Dinge und stehen
  // getrennt da. Vorher stand hier fest verdrahtet „V5.1" — das war das
  // Etikett der Score-Engine, nach der nicht mehr gehandelt wird.
  // Ein Snapshot-Schema wird erst angezeigt, wenn es die Snapshot-Tabelle
  // wirklich gibt — eine Versionsnummer für etwas, das nicht existiert,
  // wäre genau die Art Behauptung, die wir heute überall entfernt haben.
  return (
    <div
      className="text-[10px] text-zinc-500 font-mono px-1 leading-relaxed"
      title={`Regel-Konstanten ${KONSTANTEN_HASH} · App ${BUILD_VERSION}`}
    >
      <div>
        Produkt <span className="text-emerald-400">{PRODUKT_VERSION}</span> ·{' '}
        <span className="text-cockpit-success">●</span> live
      </div>
      <div>
        {REGEL_LABEL} v{REGEL_VERSION} · build {BUILD_TIME}
      </div>
    </div>
  );
}
